import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExportButton } from '@/components/common/ExportButton';
import { Snippet } from '@/components/common/Snippet';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useRecordingLabel } from '@/hooks/useRecordingLabel';
import { audioAt, clock } from '@/lib/audio';
import { fetchCadeAudioTopics } from '@/services/cade';
import type { CadeAudioTopics, CadeTopicList, CadeTopicPassage } from '@/services/types';

const PAGE_SIZE = 20;
const TOPICS = ['market', 'harm', 'remedy', 'settlement'] as const;

/**
 * Where a topic is MENTIONED in the recorded sessions: relevant market, theory of harm,
 * remedy, settlement and dosimetry.
 *
 * Found by the words used, in a machine transcript. It is not a coded field: it says where to
 * listen, not what the Tribunal decided about the topic, no speaker is identified, and a
 * passage is tied to no case because the audio does not carry the case number reliably. The
 * notice sits above the list and travels in the export.
 */
export function CadeAudioTopicsPage() {
  const { t } = useTranslation(['cade']);
  const [params, setParams] = useSearchParams();
  const topic = params.get('topic');
  const recording = params.get('recording');

  const counts = useInfiniteQuery({
    queryKey: ['cade', 'audio', 'topics', 'counts'],
    queryFn: () => fetchCadeAudioTopics({}),
    initialPageParam: 0,
    getNextPageParam: () => undefined,
  });
  const header = counts.data?.pages[0];

  const choose = (next: string) => {
    const out = new URLSearchParams();
    out.set('topic', next);
    if (recording) out.set('recording', recording);
    setParams(out);
  };
  const clearRecording = () => {
    const out = new URLSearchParams();
    if (topic) out.set('topic', topic);
    setParams(out);
  };

  return (
    <div className="space-y-4 p-6" data-audio-topics>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:audio.topics.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:audio.topics.subtitle')}</p>
      </header>
      <p
        role="note"
        className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground"
        data-machine-transcript
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
        {t('cade:audio.topics.notice')}
      </p>
      {header ? <AsOfStrip asOf={header.as_of} /> : null}

      <div className="flex flex-wrap gap-2" role="group" aria-label={t('cade:audio.topics.pick')}>
        {TOPICS.map((name) => {
          const count = header?.topics.value?.counts.find((c) => c.topic === name);
          return (
            <button
              key={name}
              type="button"
              aria-pressed={topic === name}
              onClick={() => choose(name)}
              className={[
                'rounded-md border px-3 py-1.5 text-sm',
                topic === name ? 'bg-primary text-primary-foreground' : 'text-foreground',
              ].join(' ')}
            >
              {t(`cade:audio.topics.names.${name}`)}
              {count ? (
                <span className="ml-1.5 text-xs opacity-80">
                  {t('cade:audio.topics.count', {
                    passages: count.passages,
                    recordings: count.recordings,
                  })}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {recording ? (
        <p className="text-xs text-muted-foreground" data-recording-filter>
          {t('cade:audio.topics.limitedTo', { recording })}{' '}
          <button type="button" onClick={clearRecording} className="text-primary underline">
            {t('cade:audio.topics.clear')}
          </button>
        </p>
      ) : null}

      {topic ? (
        <Passages topic={topic} recording={recording} />
      ) : (
        <p className="text-sm text-muted-foreground" data-topic-prompt>
          {t('cade:audio.topics.prompt')}
        </p>
      )}
    </div>
  );
}

function Passages({ topic, recording }: { topic: string; recording: string | null }) {
  const { t } = useTranslation(['cade']);
  const { data, isPending, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['cade', 'audio', 'topics', topic, recording],
      queryFn: ({ pageParam }) =>
        fetchCadeAudioTopics({ topic, recording, limit: PAGE_SIZE, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (last: CadeAudioTopics, all: CadeAudioTopics[]) => {
        const loaded = all.reduce((n, page) => n + (page.topics.value?.passages.length ?? 0), 0);
        return loaded < (last.topics.value?.total ?? 0) ? loaded : undefined;
      },
    });
  if (isPending) return <SkeletonLoader rows={4} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;

  const first = data.pages[0];
  const passages = data.pages.flatMap((page) => page.topics.value?.passages ?? []);
  const exportParams = new URLSearchParams({ topic });
  if (recording) exportParams.set('recording', recording);
  return (
    <section className="space-y-3" data-topic-results>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          {t(`cade:audio.topics.names.${topic}`)}
        </h2>
        <ExportButton path={`/api/v1/cade/export/audio/topics?${exportParams.toString()}`} />
      </div>
      <Answer answer={first.topics} help={t('cade:audio.topics.help')}>
        {(list: CadeTopicList) => (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t('cade:audio.topics.shown', { shown: passages.length, total: list.total })}
            </p>
            <ul className="space-y-2">
              {passages.map((passage) => (
                <PassageCard key={`${passage.recording.key}-${passage.seg}`} passage={passage} />
              ))}
            </ul>
            {hasNextPage ? (
              <button
                type="button"
                disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
                className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-50"
              >
                {t('cade:audio.more')}
              </button>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}

function PassageCard({ passage }: { passage: CadeTopicPassage }) {
  const { t } = useTranslation(['cade']);
  const label = useRecordingLabel();
  const link = audioAt(passage.recording.audio_url, passage.start_s);
  return (
    <li className="space-y-1 rounded-md border p-3" data-topic-passage>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <Link
          to={`/cade/audio/${passage.recording.key}`}
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          {label(passage.recording)}
        </Link>
        <span className="tabular-nums">{clock(passage.start_s)}</span>
      </div>
      <p className="text-sm text-foreground">
        <Snippet text={passage.text_marked} />
      </p>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          >
            <ExternalLink className="h-3 w-3" aria-hidden />
            {t('cade:audio.listen', { time: clock(passage.start_s) })}
          </a>
        ) : null}
        <Link
          to={`/cade/audio/${passage.recording.key}/transcript#seg-${passage.seg}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {t('cade:audio.detail.seeInTranscript')}
        </Link>
      </div>
    </li>
  );
}
